import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { fromEvent, merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, filter, map, shareReplay, take, takeUntil } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { AuthService } from '../core/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { UserOptionsMenuComponent } from './dialogs/user-options-menu/user-options-menu.component';
import { NotificationsDialogComponent } from './dialogs/notifications-dialog/notifications-dialog.component';
import { ChangeCompanyComponent } from './dialogs/change-company/change-company.component';
import { UserProfileService } from '../core/services/user-profile.service';
import { CustomCompanyService } from '../core/services/company.service';
import { ThemeMode, ThemeService } from '../core/services/theme.service';
import { NotificationsService } from '@core/api/notifications.service';
import { NotificationEventType } from '@core/model/notificationEventType';
import { PushNotificationQueryResponse } from '@core/model/pushNotificationQueryResponse';
import { PushNotificationType } from '@core/model/pushNotificationType';
import { SignalRService } from '@core/signalr.service';
import { PagUnreadPushNotificationsResponse } from '@core/model/pagUnreadPushNotificationsResponse';

interface NotificationView {
  id: string;
  message: string;
  rawMessage?: string;
  createdAt?: string;
  pushType?: PushNotificationType;
  eventType?: NotificationEventType;
}

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  standalone: false,
})
export class NavigationComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatSidenav;
  @ViewChild('drawerContainer') drawerContainer!: MatSidenavContainer;
  
  selectedLanguage = '';
  themeMode: ThemeMode = 'light';
  userLanguages: any[] = [];
  selectedCompany: { name: string, legalName: string };
  companies: { name: string, legalName: string }[] = [];
  isHandset = false;
  unreadNotificationsCount = 0;
  unreadNotifications: NotificationView[] = [];
  pagedNotifications: NotificationView[] = [];
  isNotificationsLoading = false;
  isNotificationsLoadingMore = false;
  hasMoreNotifications = true;
  notificationsContinuationToken: string | null = null;
  private notificationsDialogRef = {
    list: [] as NotificationView[],
    isLoading: false,
    isLoadingMore: false,
    hasMore: true
  };
  private destroy$ = new Subject<void>();

  private readonly messageMap: Record<string, { successKey: string; errorKey: string }> = {
    DigitalizationCreated: {
      successKey: 'ALERTS.DIGITALIZATION_UPDATED_SUCCESS',
      errorKey: 'ALERTS.DIGITALIZATION_UPDATE_ERROR'
    },
    ImportFinalized: {
      successKey: 'ALERTS.PRODUCTS_IMPORT_SUCCESS',
      errorKey: 'ALERTS.PRODUCTS_IMPORT_ERROR'
    },
    ImportOwnProductsFinalized: {
      successKey: 'ALERTS.PRODUCTS_IMPORT_SUCCESS',
      errorKey: 'ALERTS.PRODUCTS_IMPORT_ERROR'
    },
    ImportSupplierProductsFinalized: {
      successKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_PRODUCTS_SUCCESS',
      errorKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR'
    },
    ImportSuppliersProductsFinalized: {
      successKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_PRODUCTS_SUCCESS',
      errorKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR'
    },
    ImportSuppliersFinalized: {
      successKey: 'ALERTS.SUPPLIER_IMPORTED_SUCCESS',
      errorKey: 'ALERTS.SUPPLIER_IMPORTED_ERROR'
    },
    ImportDistributorsSuppliersFinalized: {
      successKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_SUCCESS',
      errorKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR'
    },
    ImportDistributorSupplierProductsFinalized: {
      successKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_SUCCESS',
      errorKey: 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR'
    },
    ProductCreated: {
      successKey: 'ALERTS.PRODUCT_SAVED_SUCCESS',
      errorKey: 'ALERTS.PRODUCT_SAVE_ERROR'
    },
    ProductUpdated: {
      successKey: 'ALERTS.PRODUCT_SAVED_SUCCESS',
      errorKey: 'ALERTS.PRODUCT_SAVE_ERROR'
    },
    ProductDeleted: {
      successKey: 'ALERTS.PRODUCTS_DELETED_SUCCESS',
      errorKey: 'ALERTS.PRODUCTS_DELETE_ERROR'
    },
    ProductCreatedByDigitalization: {
      successKey: 'ALERTS.DIGITALIZATION_PRODUCT_CREATED',
      errorKey: 'ALERTS.DIGITALIZATION_PRODUCT_CREATING_ERROR'
    },
    ProductUpdatedByDigitalization: {
      successKey: 'ALERTS.DIGITALIZATION_PRODUCT_UPDATED',
      errorKey: 'ALERTS.DIGITALIZATION_PRODUCT_UPDATING_ERROR'
    },
    MasiveProductFamiliesUpdated: {
      successKey: 'PRODUCTS.ALERTS.PRODUCTS_SAVED_SUCCESS',
      errorKey: 'PRODUCTS.ALERTS.PRODUCTS_SAVED_ERROR'
    },
    MasiveProductStatusUpdated: {
      successKey: 'PRODUCTS.ALERTS.PRODUCTS_STATUS_UPDATED_SUCCESS',
      errorKey: 'PRODUCTS.ALERTS.PRODUCTS_STATUS_UPDATED_ERROR'
    },
    MasiveProductDeleted: {
      successKey: 'PRODUCTS.ALERTS.PRODUCTS_DELETED_SUCCESS',
      errorKey: 'PRODUCTS.ALERTS.PRODUCTS_DELETED_ERROR'
    },
    ProductMainImageUpdated: {
      successKey: 'PRODUCTS.ALERTS.MAIN_IMAGE_UPDATED_SUCCESS',
      errorKey: 'PRODUCTS.ALERTS.MAIN_IMAGE_UPDATED_ERROR'
    },
    ConnectorJobFinished: {
      successKey: 'CHANNELS.ALERTS.CONNECTOR_EXPORT_EXCEL_EXECUTED_SUCCESS',
      errorKey: 'CHANNELS.ALERTS.CONNECTOR_UPDATE_ERROR'
    }
  };

  get isCompanySelectorDisabled(): boolean {
    return this.companies.length <= 1;
  }

  get isDarkMode(): boolean {
    return this.themeMode === 'dark';
  }

  get companyLogo(): string | null {
    const theme = this.themeService.getCurrentTheme();
    return theme?.logo || null;
  }

  get companyIcon(): string | null {
    const theme = this.themeService.getCurrentTheme();
    return theme?.icon || null;
  }

  get companySmallIcon(): string | null {
    const theme = this.themeService.getCurrentTheme();
    return theme?.smallIcon || null;
  }

  get showCompanyName(): boolean {
    const theme = this.themeService.getCurrentTheme();
    // Mostrar nombre solo si NO tiene icon completo
    // Si tiene smallIcon o no tiene nada, mostrar el nombre
    return !theme?.icon;
  }

  get hasCustomStyles(): boolean {
    const theme = this.themeService.getCurrentTheme();
    // Solo mostrar "powered by" si la empresa tiene estilos personalizados explícitamente
    return theme?.isCustomized === true;
  }

  constructor(
    private authService: AuthService,
    private oauthService: OAuthService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private userProfileService: UserProfileService,
    private companyService: CustomCompanyService,
    private themeService: ThemeService,
    private notificationsService: NotificationsService,
    private signalRService: SignalRService,
    private router: Router
  ) {
    this.companies.push({ name: this.companyService.getCompanyName()!, legalName: this.companyService.getCompanyLegalName()! });
    //this.companies.push({ name: 'Company X', legalName: 'Legal X' }, { name: 'Company Y', legalName: 'Legal Y' }, { name: 'Company Z', legalName: 'Legal Z' });
    this.selectedCompany = this.companies[0];


    const browserLang = navigator.language.split('-')[0];
    const profile = this.userProfileService.getProfile();
    this.userLanguages = profile?.enabledLanguages || [];

    if (profile && profile.language) {
      this.translate.setDefaultLang(profile.language);
      this.translate.use(profile.language);
    } else {
      this.translate.setDefaultLang(browserLang);
      this.translate.use(browserLang);
    }

    // Suscribirse al estado de handset y almacenarlo
    this.isHandset$.pipe(takeUntil(this.destroy$)).subscribe(isHandset => {
      this.isHandset = isHandset;
    });
  }

  private breakpointObserver = inject(BreakpointObserver);

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  ngOnInit(): void {
    this.themeService.mode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => this.themeMode = mode);

    this.refreshUnreadNotifications();
    this.subscribeToNotificationUpdates();

    this.authService.isDoneLoading$.pipe(
      filter(done => done),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.authService.companyLoadFailed$.getValue() && this.userProfileService.isAdminUser()) {
        this.openChangeCompanyDialog();
      }
    });
  }

  ngAfterViewInit(): void {
    merge(
      this.isHandset$,
      fromEvent(window, 'resize').pipe(debounceTime(100))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.scheduleSidenavMarginUpdate());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout() {
    this.authService.logout();
  }

  toggleThemeMode(): void {
    this.themeService.toggleMode();
  }

  isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  userHasProfile(): boolean {
    return this.authService.userHasProfile();
  }

  userHasBeenOnboarded(): boolean {
    return this.authService.userHasBeenOnboarded();
  }

  getSelectedLanguageFlag(): string {
    const selectedLang = this.userLanguages.find(lang => lang.code === this.selectedLanguage);
    return selectedLang ? selectedLang.flag : 's/n';
  }

  openUserOptionsMenu() {
    this.dialog.open(UserOptionsMenuComponent, {
      width: '400px',
      disableClose: true
    });
  }

  openNotificationsDialog() {
    this.isNotificationsLoading = true;
    this.pagedNotifications.length = 0;
    this.hasMoreNotifications = true;
    this.notificationsContinuationToken = null;

    this.notificationsDialogRef.list = this.pagedNotifications;
    this.notificationsDialogRef.isLoading = true;
    this.notificationsDialogRef.isLoadingMore = false;
    this.notificationsDialogRef.hasMore = true;

    this.dialog.open(NotificationsDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        notificationsRef: this.notificationsDialogRef,
        onMarkAsReadAndNavigate: (notification: NotificationView) => this.markNotificationAsReadAndNavigate(notification),
        onMarkAsReadOnly: (notification: NotificationView, event: Event) => this.markNotificationAsReadOnly(notification, event as MouseEvent),
        onNotificationClosed: (notification: NotificationView) => {
          const index = this.notificationsDialogRef.list.findIndex((item) => item.id === notification.id);
          if (index > -1) {
            this.notificationsDialogRef.list.splice(index, 1);
          }
          this.unreadNotificationsCount = Math.max(0, this.unreadNotificationsCount - 1);
        },
        getIconForType: (type: PushNotificationType) => this.getIconForType(type),
        getClassForType: (type: PushNotificationType) => this.getClassForType(type),
        onDialogOpened: () => {},
        onLoadMore: () => this.loadMoreNotifications()
      }
    });

    this.notificationsService.v1NotificationsApiPushNotificationsUnreadPagedPost(8, undefined)
      .pipe(take(1), catchError(() => of({ notifications: [] } as PagUnreadPushNotificationsResponse)))
      .subscribe((pagedResponse) => {
        const mappedPaged = (pagedResponse?.notifications || [])
          .map((notification: PushNotificationQueryResponse) => this.toNotificationView(notification));
        const [, visiblePaged] = this.partitionSwitchCompany(mappedPaged);

        this.pagedNotifications.push(...visiblePaged);
        this.isNotificationsLoading = false;
        this.notificationsDialogRef.isLoading = false;
        this.notificationsContinuationToken = pagedResponse?.continuationToken ?? null;
        this.hasMoreNotifications = !!this.notificationsContinuationToken;
        this.notificationsDialogRef.hasMore = this.hasMoreNotifications;
      });
  }

  openChangeCompanyDialog(event?: MouseEvent) {

    if (!this.userProfileService.isAdminUser()) return;

    event?.preventDefault();
    this.dialog.open(ChangeCompanyComponent, {
      width: '400px',
      disableClose: true
    });
  }

  existInModulesAndCanAccess(moduleCode: string): boolean {
    const userModules = this.userProfileService.getUserModules();
    return userModules.some((module: any) => {
      if (module.moduleCode !== moduleCode) {
        return false;
      }
      const accessLevel = this.getAccessLevelValue(module.access);
      return accessLevel > 0;
    });
  }

  private getAccessLevelValue(access: any): number {
    if (typeof access === 'number') {
      return access;
    }
    
    const accessMap: { [key: string]: number } = {
      'None': 0,
      'Read': 1,
      'Write': 2,
      'Delete': 3,
      'Full': 4
    };
    
    return accessMap[access] || 0;
  }

  hasElementsBetweenDivider1And2(): boolean {
    // Elementos del grupo 1 (antes del divisor 2)
    const group1 = this.existInModulesAndCanAccess('PRODUCTS') ||
      this.existInModulesAndCanAccess('SUPPLIERS') ||
      this.existInModulesAndCanAccess('CUSTOMERS') ||
      this.existInModulesAndCanAccess('DISTRIBUTORS');
    
    // Elementos del grupo 2 (después del divisor 2)
    const group2 = this.existInModulesAndCanAccess('CHANNELS');
    
    // Mostrar separador solo si hay elementos en ambos lados
    return group1 && group2;
  }

  hasElementsBetweenDivider2And3(): boolean {
    // Elementos del grupo 2 (antes del divisor 3)
    const group2 = this.existInModulesAndCanAccess('CHANNELS');
    
    // Elementos del grupo 3 (después del divisor 3)
    const group3 = this.existInModulesAndCanAccess('DIGITALIZATIONS') ||
      this.existInModulesAndCanAccess('ALIGMENTS') ||
      this.existInModulesAndCanAccess('TRANSLATIONS') ||
      this.existInModulesAndCanAccess('MEDIA');
    
    // Mostrar separador solo si hay elementos en ambos lados
    return group2 && group3;
  }

  hasElementsBetweenDivider3And4(): boolean {
    // Elementos del grupo 3 (antes del divisor 4)
    const group3 = this.existInModulesAndCanAccess('DIGITALIZATIONS') ||
      this.existInModulesAndCanAccess('ALIGMENTS') ||
      this.existInModulesAndCanAccess('TRANSLATIONS') ||
      this.existInModulesAndCanAccess('MEDIA');
    
    // Elementos del grupo 4 (después del divisor 4)
    const group4 = this.existInModulesAndCanAccess('CONFLICTS') ||
      this.existInModulesAndCanAccess('CLAIMS');
    
    // Mostrar separador solo si hay elementos en ambos lados
    return group3 && group4;
  }

  closeDrawerOnMenuClick(): void {
    // Solo cerrar el drawer si estamos en mobile
    if (this.isHandset && this.drawer) {
      this.drawer.close();
    }
  }

  private scheduleSidenavMarginUpdate(): void {
    requestAnimationFrame(() => {
      this.drawerContainer?.updateContentMargins();
      setTimeout(() => this.drawerContainer?.updateContentMargins(), 350);
    });
  }

  trackByNotificationId(_: number, notification: NotificationView): string {
    return notification.id || notification.createdAt || '';
  }

  getIconForType(pushType?: PushNotificationType): string {
    switch (pushType) {
      case 'Success':
        return 'check_circle';
      case 'Error':
        return 'error';
      case 'Info':
      default:
        return 'info';
    }
  }

  getClassForType(pushType?: PushNotificationType): string {
    switch (pushType) {
      case 'Success':
        return 'notification-icon success';
      case 'Error':
        return 'notification-icon error';
      case 'Info':
      default:
        return 'notification-icon info';
    }
  }

  markNotificationAsReadAndNavigate(notification: NotificationView): void {
    if (!notification.id) {
      return;
    }

    this.notificationsService.v1NotificationsApiPushNotificationsIdReadPatch(notification.id)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe(() => {
        this.unreadNotifications = this.unreadNotifications.filter((item) => item.id !== notification.id);
        this.unreadNotificationsCount = this.unreadNotifications.length;
        this.navigateForNotification(notification);
      });
  }

  markNotificationAsReadOnly(notification: NotificationView, event: MouseEvent): void {
    event.stopPropagation();
    if (!notification.id) {
      return;
    }

    this.notificationsService.v1NotificationsApiPushNotificationsIdReadPatch(notification.id)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe(() => {
        this.unreadNotifications = this.unreadNotifications.filter((item) => item.id !== notification.id);
        this.unreadNotificationsCount = this.unreadNotifications.length;
      });
  }

  private refreshUnreadNotifications(): void {
    this.isNotificationsLoading = true;
    this.notificationsService.v1NotificationsApiPushNotificationsUnreadGet()
      .pipe(
        take(1),
        catchError(() => of([]))
      )
      .subscribe((notifications: PushNotificationQueryResponse[]) => {
        const mappedNotifications = (notifications || [])
          .map((notification: PushNotificationQueryResponse) => this.toNotificationView(notification));
        const [hiddenNotifications, visibleNotifications] = this.partitionSwitchCompany(mappedNotifications);

        hiddenNotifications.forEach((notification) => {
          if (notification.id) {
            this.markNotificationAsRead(notification.id);
          }
        });

        this.unreadNotifications = visibleNotifications;
        this.unreadNotificationsCount = visibleNotifications.length;
        this.isNotificationsLoading = false;
      });
  }

  private subscribeToNotificationUpdates(): void {
    merge(
      this.signalRService.onDigitalizationCreated(),
      this.signalRService.onImportFinalized(),
      this.signalRService.onImportOwnProductsFinalized(),
      this.signalRService.onImportSupplierProductsFinalized(),
      this.signalRService.onImportSuppliersFinalized(),
      this.signalRService.onImportDistributorsSuppliersFinalized(),
      this.signalRService.onImportDistributorSupplierProductsFinalized(),
      this.signalRService.onProductCreated(),
      this.signalRService.onProductDeleted(),
      this.signalRService.onProductCreatedByDigitalization(),
      this.signalRService.onProductUpdatedByDigitalization(),
      this.signalRService.onProductsValidatedFromExternalSource(),
      this.signalRService.onProductMassiveFamilyUpdated(),
      this.signalRService.onProductMassiveStatusUpdated(),
      this.signalRService.onProductMassiveDeleted(),
      this.signalRService.onProductMainImageUpdated(),
      this.signalRService.onConnectorJobFinished(),
      this.signalRService.onProductAlignmentConflictNotificationCreated()
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshUnreadNotifications();
      });
  }

  private toNotificationView(notification: PushNotificationQueryResponse): NotificationView {
    return {
      id: notification.id || '',
      message: this.resolveMessage(notification),
      rawMessage: notification.message || '',
      createdAt: notification.createdAt,
      pushType: notification.pushType,
      eventType: notification.eventType
    };
  }

  private resolveMessage(notification: PushNotificationQueryResponse): string {
    const eventType = notification.eventType;
    const eventTypeValue = (eventType as unknown as string) || '';
    const pushType = notification.pushType;
    if (eventTypeValue === 'ProductAlignmentConflictNotificationCreated') {
      return this.translate.instant('ALERTS.CONFLICT_ASSIGNED');
    }

    const mapping = eventTypeValue ? this.messageMap[eventTypeValue] : undefined;

    if (mapping && (pushType === 'Success' || pushType === 'Error')) {
      const key = pushType === 'Success' ? mapping.successKey : mapping.errorKey;
      return this.translate.instant(key);
    }

    if (eventTypeValue.startsWith('Import') && (pushType === 'Success' || pushType === 'Error')) {
      const importKey = this.resolveImportMessageKey(eventTypeValue, notification.message, pushType);
      return this.translate.instant(importKey);
    }

    const structuredMessage = this.resolveStructuredMessage(notification.message, pushType);
    if (structuredMessage) {
      return structuredMessage;
    }

    return notification.message || '';
  }

  private resolveStructuredMessage(
    rawMessage: string | null | undefined,
    pushType?: PushNotificationType
  ): string | null {
    const structuredNavigation = this.getStructuredNavigation(rawMessage);
    if (!structuredNavigation) {
      return null;
    }

    if (structuredNavigation.route.startsWith('/providers') || structuredNavigation.route.startsWith('/distributors')) {
      if (pushType === 'Success' || pushType === 'Error') {
        return this.translate.instant(
          pushType === 'Success'
            ? 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_SUCCESS'
            : 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR'
        );
      }
      return this.translate.instant('MENU.DISTRIBUTORS');
    }

    if (structuredNavigation.route.startsWith('/products')) {
      if (pushType === 'Success' || pushType === 'Error') {
        return this.translate.instant(
          pushType === 'Success'
            ? 'ALERTS.PRODUCTS_IMPORT_SUCCESS'
            : 'ALERTS.PRODUCTS_IMPORT_ERROR'
        );
      }
      return this.translate.instant('MENU.PRODUCTS');
    }

    if (structuredNavigation.route.startsWith('/channels')) {
      if (pushType === 'Success' || pushType === 'Error') {
        return this.translate.instant(
          pushType === 'Success'
            ? 'CHANNELS.ALERTS.CONNECTOR_EXPORT_EXCEL_EXECUTED_SUCCESS'
            : 'CHANNELS.ALERTS.CONNECTOR_UPDATE_ERROR'
        );
      }
      return this.translate.instant('MENU.CHANNELS');
    }

    if (structuredNavigation.route.startsWith('/customers')) {
      return this.translate.instant('MENU.CLIENTS');
    }

    if (structuredNavigation.route.startsWith('/claims')) {
      return this.translate.instant('MENU.CLAIMS');
    }

    if (structuredNavigation.route.startsWith('/conflicts')) {
      return this.translate.instant('MENU.CONFLICTS');
    }

    return null;
  }

  private resolveImportMessageKey(
    eventTypeValue: string,
    rawMessage: string | null | undefined,
    pushType: PushNotificationType
  ): string {
    if (eventTypeValue === 'ImportSupplierProductsFinalized' || eventTypeValue === 'ImportSuppliersProductsFinalized') {
      return pushType === 'Success'
        ? 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_PRODUCTS_SUCCESS'
        : 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR';
    }

    if (eventTypeValue === 'ImportSuppliersFinalized') {
      return pushType === 'Success'
        ? 'ALERTS.SUPPLIER_IMPORTED_SUCCESS'
        : 'ALERTS.SUPPLIER_IMPORTED_ERROR';
    }

    const structuredNavigation = this.getStructuredNavigation(rawMessage);
    if (structuredNavigation) {
      if (structuredNavigation.route.startsWith('/providers') || structuredNavigation.route.startsWith('/distributors')) {
        return pushType === 'Success'
          ? 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_SUCCESS'
          : 'DISTRIBUTORS.PAGES.DETAIL-PAGE.IMPORT_ERROR';
      }

      if (structuredNavigation.route.startsWith('/products')) {
        return pushType === 'Success'
          ? 'ALERTS.PRODUCTS_IMPORT_SUCCESS'
          : 'ALERTS.PRODUCTS_IMPORT_ERROR';
      }
    }

    return pushType === 'Success'
      ? 'ALERTS.PRODUCTS_IMPORT_SUCCESS'
      : 'ALERTS.PRODUCTS_IMPORT_ERROR';
  }

  private navigateForNotification(notification: NotificationView): void {
    const navigation = this.resolveNotificationNavigation(notification);
    if (!navigation) {
      return;
    }

    if (navigation.id && navigation.route.includes(':id')) {
      this.router.navigate([navigation.route.replace(':id', navigation.id)]);
      return;
    }

    if (navigation.id) {
      this.router.navigate([navigation.route, navigation.id]);
      return;
    }

    this.router.navigate([navigation.route]);
  }

  private resolveNotificationNavigation(notification: NotificationView): { route: string; id?: string } | null {
    const structuredNavigation = this.getStructuredNavigation(notification.rawMessage);
    if (structuredNavigation) {
      return structuredNavigation;
    }

    const rawId = this.extractGuid(notification.rawMessage);
    return this.getNavigationForEvent(notification.eventType, rawId);
  }

  private getStructuredNavigation(rawMessage?: string | null): { route: string; id?: string } | null {
    if (!rawMessage) {
      return null;
    }

    const trimmedMessage = rawMessage.trim();
    if (!trimmedMessage.startsWith('{')) {
      return null;
    }

    try {
      const payload = JSON.parse(trimmedMessage) as {
        route?: string;
        module?: string;
        moduleCode?: string;
        id?: string;
        entityId?: string;
        resourceId?: string;
      };

      const route = this.normalizeRoute(payload.route, payload.module, payload.moduleCode);
      if (!route) {
        return null;
      }

      const targetId = this.extractGuid(payload.id, payload.entityId, payload.resourceId);
      return targetId ? { route, id: targetId } : { route };
    } catch {
      return null;
    }
  }

  private normalizeRoute(route?: string, module?: string, moduleCode?: string): string | null {
    if (route && route.trim().length > 0) {
      return route.startsWith('/') ? route : `/${route}`;
    }

    const moduleValue = (moduleCode || module || '').trim().toUpperCase();
    switch (moduleValue) {
      case 'PRODUCTS':
        return '/products';
      case 'SUPPLIERS':
      case 'PROVIDERS':
        return '/providers';
      case 'DIGITALIZATIONS':
        return '/digitalitzations';
      case 'CHANNELS':
        return '/channels';
      case 'CLAIMS':
        return '/claims';
      case 'CONFLICTS':
        return '/conflicts';
      case 'DISTRIBUTORS':
        return '/distributors';
      case 'CUSTOMERS':
        return '/customers';
      case 'TRANSLATIONS':
        return '/translations';
      case 'MEDIA':
        return '/multimedia';
      case 'ALIGNMENTS':
        return '/alignments';
      default:
        return null;
    }
  }

  private getNavigationForEvent(eventType?: NotificationEventType, targetId?: string): { route: string; id?: string } | null {
    const eventTypeValue = (eventType as unknown as string) || '';

    if (eventTypeValue === 'ProductAlignmentConflictNotificationCreated') {
      return targetId
        ? { route: '/conflicts/detail', id: targetId }
        : { route: '/conflicts' };
    }

    switch (eventTypeValue) {
      case 'DigitalizationCreated':
        return targetId
          ? { route: '/digitalitzations/detail', id: targetId }
          : { route: '/digitalitzations' };
      case 'ImportFinalized':
      case 'ImportOwnProductsFinalized':
      case 'ProductsValidatedFromExternalSource':
      case 'MasiveProductFamiliesUpdated':
      case 'MasiveProductStatusUpdated':
      case 'MasiveProductDeleted':
      case 'LabelCreated':
        return { route: '/products' };
      case 'ImportSupplierProductsFinalized':
      case 'ImportSuppliersProductsFinalized':
      case 'ImportSuppliersFinalized':
        return { route: '/providers' };
      case 'ImportDistributorsSuppliersFinalized':
      case 'ImportDistributorSupplierProductsFinalized':
        return { route: '/distributors' };
      case 'ProductCreated':
      case 'ProductUpdated':
      case 'ProductDeleted':
      case 'ProductCreatedByDigitalization':
      case 'ProductUpdatedByDigitalization':
      case 'ProductMainImageUpdated':
        return targetId
          ? { route: '/products/detail', id: targetId }
          : { route: '/products' };
      case 'ConnectorJobFinished':
        return targetId
          ? { route: '/channels/connector-statics-detail', id: targetId }
          : { route: '/channels' };
      case 'SwitchCompany':
      default:
        return null;
    }
  }

  private extractGuid(...values: Array<string | undefined>): string | undefined {
    const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return values.find((value) => !!value && guidPattern.test(value));
  }

  private partitionSwitchCompany(notifications: NotificationView[]): [NotificationView[], NotificationView[]] {
    const hidden: NotificationView[] = [];
    const visible: NotificationView[] = [];

    notifications.forEach((notification) => {
      if (notification.eventType === 'SwitchCompany') {
        hidden.push(notification);
      } else {
        visible.push(notification);
      }
    });

    return [hidden, visible];
  }

  private markNotificationAsRead(notificationId: string): void {
    this.notificationsService.v1NotificationsApiPushNotificationsIdReadPatch(notificationId)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe();
  }

  private loadMoreNotifications(): void {
    if (this.isNotificationsLoading || this.isNotificationsLoadingMore || !this.hasMoreNotifications) {
      return;
    }

    this.isNotificationsLoadingMore = true;
    this.notificationsDialogRef.isLoadingMore = true;

    this.notificationsService.v1NotificationsApiPushNotificationsUnreadPagedPost(8, this.asJsonStringBody(this.notificationsContinuationToken))
      .pipe(
        take(1),
        catchError(() => of({ notifications: [], continuationToken: null } as PagUnreadPushNotificationsResponse))
      )
      .subscribe((pagedResponse) => {
        const mappedPaged = (pagedResponse?.notifications || [])
          .map((notification: PushNotificationQueryResponse) => this.toNotificationView(notification));
        const [, visiblePaged] = this.partitionSwitchCompany(mappedPaged);

        this.pagedNotifications.push(...visiblePaged);
        this.notificationsContinuationToken = pagedResponse?.continuationToken ?? null;
        this.hasMoreNotifications = !!this.notificationsContinuationToken;
        this.isNotificationsLoadingMore = false;

        this.notificationsDialogRef.isLoadingMore = false;
        this.notificationsDialogRef.hasMore = this.hasMoreNotifications;
      });
  }

  private asJsonStringBody(value: string | null): string | undefined {
    if (!value) {
      return undefined;
    }
    return JSON.stringify(value);
  }

}
